import { Brick, registerBrick, Query, ErrorFlow } from 'olympe';

const SQL_MAX_PARAMS = 2100;
const SAFE_MAX_PARAMS = 2000;

const cleanseQuery = (query) => {
    const pattern = /':p(\d+)'/g;
    return query.replace(pattern, ":p$1");
};

const isHistoryTable = (tableName) =>
    tableName.toLowerCase().endsWith("_history");

const isHistoryTimestampColumn = (columnName) => {
    const key = columnName.toLowerCase();
    return key === "last_updated" || key === "last_updt_dttm";
};

const chunkArray = (array, size) => {
    const chunks = [];

    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }

    return chunks;
};

const getSafeBatchSize = (columnCount) => {
    const safeSize = Math.floor(SAFE_MAX_PARAMS / columnCount);
    return Math.max(1, safeSize);
};

const removeBindingByPlaceholder = (bindings, placeholder) => {
    if (!placeholder || !placeholder.startsWith(":")) return;

    const paramName = placeholder.replace(":", "");

    if (bindings[paramName] !== undefined) {
        delete bindings[paramName];
    }
};

const getUniqueHistoryTimestampSql = (historyCounter) => {
    const offset = historyCounter.value++;
    return `DATEADD(MILLISECOND, ${offset}, SYSDATETIME())`;
};

const seperateValueBasedonTableName = (cleanedData) =>
    Object.entries(cleanedData).reduce((acc, curr) => {
        const key = curr[0];

        if (key !== "#tag" && key !== "#type") {
            curr[1].tableName.forEach((tableName, index) => {
                if (!acc[tableName]) {
                    acc[tableName] = {
                        queryOperation: curr[1].queryOperations[index],
                        entries: []
                    };
                }

                acc[tableName].entries.push({
                    key,
                    value: curr[1].value[index]
                });
            });
        }

        return acc;
    }, {});

const generateMapper = (modelTag) => {
    const paramConfigs =
        Query.instancesOf(modelTag, false, "server").executeFromCache();

    return paramConfigs.reduce((acc, curr) => {
        const column = curr.get("0185c081050d996c424c");
        const field = curr.get("0185c081098c285960d8");
        const queryOperation = curr.get("01879d93b621833b5c5f");
        const isPrimaryKey = !!curr.get("0187b278b39c26a45233");

        const tableName =
            `${curr.get("01862c935347456d04b2")}.${curr.get("0185c080fff9d4533458")}`;

        if (!acc[field]) {
            acc[field] = {
                tableNames: [],
                isPrimaryKey: [],
                queryOperations: []
            };
        }

        acc[field] = {
            tableNames: [...acc[field].tableNames, tableName],
            columnName: `${column}`,
            isPrimaryKey: [...acc[field].isPrimaryKey, isPrimaryKey],
            queryOperations: [...acc[field].queryOperations, queryOperation]
        };

        return acc;
    }, {});
};

const generateConflictKeys = (mapper) => {
    const conflictKeys = {};

    Object.entries(mapper).forEach(([field, props]) => {
        props.tableNames.forEach((table, index) => {
            if (!conflictKeys[table]) {
                conflictKeys[table] = [];
            }

            if (props.isPrimaryKey[index]) {
                conflictKeys[table].push(props.columnName);
            }
        });
    });

    return conflictKeys;
};

const createBindingsForBatch = ({ mapper, data, bindings }) => {
    const finalData = {};

    const createBinding = ({ value }) => {
        const newParam = `p${bindings.paramIndex}`;
        const binding = `:${newParam}`;

        bindings[newParam] = value;
        bindings.paramIndex += 1;

        return binding;
    };

    Object.keys(mapper).forEach((key) => {
        const column = mapper[key]?.columnName;

        mapper[key].tableNames.forEach((table, index) => {
            const valueForBinding = data[key] === undefined ? null : data[key];

            if (column) {
                if (!finalData[column]) {
                    finalData[column] = {
                        tableName: [],
                        queryOperations: [],
                        value: []
                    };
                }

                const newBinding = createBinding({
                    value: valueForBinding
                });

                finalData[column].tableName.push(table);
                finalData[column].queryOperations.push(
                    mapper[key].queryOperations[index]
                );
                finalData[column].value.push(newBinding);
            }
        });
    });

    return finalData;
};

const buildBatchQueryForTable = ({
    tableName,
    rows,
    conflictKeys,
    historyCounter
}) => {
    const columns = rows[0].map(({ key }) => key);
    const columnCount = columns.length;
    const safeBatchSize = getSafeBatchSize(columnCount);
    const rowChunks = chunkArray(rows, safeBatchSize);

    return rowChunks.map((rowChunk, batchPartIndex) => {
        const bindings = {
            paramIndex: 0
        };

        const valuesRows = rowChunk.map((entries) => {
            const values = entries.map(({ key, rawValue }) => {
                if (isHistoryTable(tableName) && isHistoryTimestampColumn(key)) {
                    return getUniqueHistoryTimestampSql(historyCounter);
                }

                const paramName = `p${bindings.paramIndex}`;
                bindings[paramName] = rawValue;
                bindings.paramIndex += 1;

                return `:${paramName}`;
            });

            return `(${values.join(", ")})`;
        });

        delete bindings.paramIndex;

        if (Object.keys(bindings).length > SQL_MAX_PARAMS) {
            throw new Error(
                `Too many SQL parameters for ${tableName}. Count: ${Object.keys(bindings).length}`
            );
        }

        if (isHistoryTable(tableName)) {
            return {
                query: cleanseQuery(`
INSERT INTO ${tableName}
(${columns.join(", ")})
VALUES
${valuesRows.join(",\n")};
`.trim()),
                bindings,
                rowCount: rowChunk.length,
                batchPartIndex
            };
        }

        const conflictCols = conflictKeys[tableName];

        if (!conflictCols || conflictCols.length === 0) {
            throw new Error(`Conflict keys not defined for table: ${tableName}`);
        }

        const sourceAlias = "source";
        const targetAlias = "target";

        const onClause = conflictCols
            .map((col) => `${targetAlias}.${col} = ${sourceAlias}.${col}`)
            .join(" AND ");

        const updateAssignments = columns
            .filter((col) => !conflictCols.includes(col))
            .map((col) => `${col} = ${sourceAlias}.${col}`)
            .join(", ");

        return {
            query: cleanseQuery(`
MERGE INTO ${tableName} AS ${targetAlias}
USING (
VALUES
${valuesRows.join(",\n")}
)
AS ${sourceAlias} (${columns.join(", ")})
ON ${onClause}
WHEN MATCHED THEN
    UPDATE SET ${updateAssignments}
WHEN NOT MATCHED THEN
    INSERT (${columns.join(", ")})
    VALUES (${columns.map((col) => `${sourceAlias}.${col}`).join(", ")});
`.trim()),
            bindings,
            rowCount: rowChunk.length,
            batchPartIndex
        };
    });
};

const generateBatchInsertOrMergeQuery = ({
    mapper,
    dataList,
    conflictKeys,
    historyCounter
}) => {
    const tableRows = {};

    dataList.forEach((data) => {
        const tempBindings = {
            paramIndex: 0
        };

        const cleanedData = createBindingsForBatch({
            mapper,
            data,
            bindings: tempBindings
        });

        const separatedByTable = seperateValueBasedonTableName(cleanedData);

        Object.keys(separatedByTable).forEach((tableName) => {
            if (!tableRows[tableName]) {
                tableRows[tableName] = [];
            }

            const entries = separatedByTable[tableName].entries.map(
                ({ key }) => ({
                    key,
                    rawValue: data[
                        Object.keys(mapper).find(
                            (field) => mapper[field].columnName === key
                        )
                    ] ?? null
                })
            );

            tableRows[tableName].push(entries);
        });
    });

    const result = [];

    Object.keys(tableRows).forEach((tableName) => {
        const rows = tableRows[tableName];

        if (!rows || rows.length === 0) {
            return;
        }

        const tableQueries = buildBatchQueryForTable({
            tableName,
            rows,
            conflictKeys,
            historyCounter
        });

        tableQueries.forEach((item) => {
            result.push({
                query: item.query,
                bindings: item.bindings,
                tableName,
                rowCount: item.rowCount,
                batchPartIndex: item.batchPartIndex
            });
        });
    });

    return result;
};

const removeIfNotInMapper = ({
    mapper,
    data,
    bindings,
    finalConditions = {},
    isUpdate = false
}) => {
    const finalData = {};

    const createBindings = ({ value, table }) => {
        if (!bindings[table]) {
            bindings[table] = {
                paramIndex: 0
            };
        }

        const newParam = `p${bindings[table].paramIndex}`;
        const binding = `:${newParam}`;

        bindings[table] = {
            ...bindings[table],
            [newParam]: value,
            paramIndex: bindings[table].paramIndex + 1
        };

        return binding;
    };

    Object.keys(mapper).forEach((key) => {
        const column = mapper[key]?.columnName;

        mapper[key].tableNames.forEach((table, index) => {
            const valueForBinding =
                data[key] === undefined && !isUpdate ? null : data[key];

            if (
                column &&
                !(isUpdate && valueForBinding === undefined && !finalConditions[column])
            ) {
                if (!finalData[column]) {
                    finalData[column] = {
                        tableName: [],
                        queryOperations: [],
                        value: []
                    };
                }

                const newBinding = createBindings({
                    value: valueForBinding,
                    table
                });

                finalData[column].tableName.push(table);
                finalData[column].queryOperations.push(
                    mapper[key].queryOperations[index]
                );
                finalData[column].value.push(newBinding);
            }
        });
    });

    Object.keys(bindings).forEach((tableName) => {
        delete bindings[tableName].paramIndex;
    });

    return finalData;
};

const buildWhereCondition = (finalConditions) => {
    return Object.keys(finalConditions)
        .map((key) => {
            const value = finalConditions[key];

            if (typeof value === "string") {
                return `${key} = '${value.replace(/'/g, "''")}'`;
            }

            if (value === null) {
                return `${key} IS NULL`;
            }

            return `${key} = ${value}`;
        })
        .join(" AND ");
};

const generateUpdateQuery = ({ mapper, updateConditions, data }) => {
    let bindings = [];

    const finalConditions = updateConditions.reduce((acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    }, {});

    const cleanedData = removeIfNotInMapper({
        mapper,
        data,
        bindings,
        finalConditions,
        isUpdate: true
    });

    const separatedByTable = seperateValueBasedonTableName(cleanedData);
    const conditionStrings = buildWhereCondition(finalConditions);

    const queries = Object.keys(separatedByTable).map((tableName) => {
        const updates = separatedByTable[tableName].entries
            .map(({ key, value }) =>
                value === "NULL" ? `${key} = NULL` : `${key} = ${value}`
            )
            .join(", ");

        return cleanseQuery(
            `UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`
        );
    });

    return {
        queries,
        bindings
    };
};

const generateDeleteQuery = ({ mapper, updateConditions, data }) => {
    let bindings = [];

    const finalConditions = updateConditions.reduce((acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    }, {});

    const cleanedData = removeIfNotInMapper({
        mapper,
        data,
        bindings,
        finalConditions,
        isUpdate: true
    });

    const separatedByTable = seperateValueBasedonTableName(cleanedData);
    const conditionStrings = buildWhereCondition(finalConditions);

    const queries = Object.keys(separatedByTable).map((tableName) => {
        const updates = separatedByTable[tableName].entries
            .map(({ key, value }) =>
                value === "NULL" ? `${key} = NULL` : `${key} = ${value}`
            )
            .join(", ");

        return cleanseQuery(
            `UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`
        );
    });

    return {
        queries,
        bindings
    };
};

const mapQueryWithBindings = ({
    queries,
    bindingsObject,
    batchType,
    batchIndex,
    rowIndex
}) => {
    const bindings = Object.values(bindingsObject);

    return queries.map((query, index) => ({
        query,
        bindings: bindings[index],
        batchType,
        batchIndex,
        rowIndex
    }));
};

const generateQuery = ({
    modelTag,
    updateConditions,
    payload,
    historyCounter
}) => {
    const { toDelete, toInsert, toUpdate } = payload;

    const mapper = generateMapper(modelTag);
    const conflictKeys = generateConflictKeys(mapper);

    const insertItems = Array.isArray(toInsert) ? toInsert : [];
    const updateItems = Array.isArray(toUpdate) ? toUpdate : [];
    const deleteItems = Array.isArray(toDelete) ? toDelete : [];

    const insertQueries = generateBatchInsertOrMergeQuery({
        mapper,
        dataList: insertItems,
        conflictKeys,
        historyCounter
    }).map((item, index) => ({
        query: item.query,
        bindings: item.bindings,
        batchType: "insert",
        batchIndex: item.batchPartIndex,
        rowIndex: null,
        tableName: item.tableName,
        rowCount: item.rowCount
    }));

    const updateChunks = chunkArray(updateItems, 100);
    const deleteChunks = chunkArray(deleteItems, 100);

    const updateQueries = updateChunks.flatMap((chunk, batchIndex) =>
        chunk.flatMap((item, rowIndex) => {
            const { queries, bindings: bindingsObject } =
                generateUpdateQuery({
                    mapper,
                    updateConditions,
                    data: item
                });

            return mapQueryWithBindings({
                queries,
                bindingsObject,
                batchType: "update",
                batchIndex,
                rowIndex
            });
        })
    );

    const deleteQueries = deleteChunks.flatMap((chunk, batchIndex) =>
        chunk.flatMap((item, rowIndex) => {
            const { queries, bindings: bindingsObject } =
                generateDeleteQuery({
                    mapper,
                    updateConditions,
                    data: item
                });

            return mapQueryWithBindings({
                queries,
                bindingsObject,
                batchType: "delete",
                batchIndex,
                rowIndex
            });
        })
    );

    console.log("BuildTransactionSQLQueryV6 Batch Summary", {
        insertCount: insertItems.length,
        updateCount: updateItems.length,
        deleteCount: deleteItems.length,
        totalQueries:
            insertQueries.length + updateQueries.length + deleteQueries.length,
        historyTimestampCount: historyCounter.value
    });

    return [
        ...insertQueries,
        ...updateQueries,
        ...deleteQueries
    ];
};

export default class BuildTransactionSQLQueryV6 extends Brick {
    update($, [modelTags, payload, updateConditions], [forwardEvent, setQuery, setErrorFlow]) {
        try {
            const historyCounter = {
                value: 0
            };

            const queries = payload.reduce((acc, curr, index) => {
                if (!modelTags[index]) {
                    throw new Error(`Missing modelTag for payload index ${index}`);
                }

                const newQuery = generateQuery({
                    modelTag: modelTags[index],
                    updateConditions: updateConditions
                        ? updateConditions[index] || []
                        : [],
                    payload: curr,
                    historyCounter
                });

                return [...acc, ...newQuery];
            }, []);

            setQuery(queries);
            forwardEvent();
        } catch (error) {
            console.log(error);
            setErrorFlow(ErrorFlow.create(`Error ${error} during the execution`));
        }
    }
}

registerBrick("019d8b764a99fe4f6a52", BuildTransactionSQLQueryV6);